/* eslint-disable @typescript-eslint/no-base-to-string */
/**
 * Generic list-card status helpers: same `columnConfig` as kanban → label + lozenge colors.
 * Workspace routes pass their kanban `columnConfig`; no workspace-specific styling here.
 */
import {token} from "@atlaskit/tokens";
import type {KanbanBoardColumnConfig} from "~/components/ottopilotDataViewer/view";
import {
    formatAnankeLozengeLabel,
    type AnankeAccentStatusLozengeStyle,
} from "@vesta/ananke/components/anankeLozenge";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";

export type ListStatusPillType =
    | "Active"
    | "Archived"
    | "Deleted"
    | "Assigned"
    | "Unassigned";

/** Reuses the kanban board column shape — list status lozenges read the same `columnConfig`. */
export type ListStatusBoardConfig = {
    columnConfig: ReadonlyArray<KanbanBoardColumnConfig>;
};

/** Same contract as {@link AnankeAccentStatusLozengeStyle} (border uses `color`). */
export type ListCardLozengeStyle = AnankeAccentStatusLozengeStyle & {
    /** @deprecated Border always matches `color`; kept for callers passing kanban-style shapes. */
    borderColor?: string;
};

export const formatListStatusLabel = formatAnankeLozengeLabel;

export type ListCardStatus = {
    name: string;
    lozengeStyle?: ListCardLozengeStyle;
    pillType?: ListStatusPillType;
};

/** When kanban columns use accent backgrounds but neutral `color.text`, derive matching accent text for list lozenges. */
export function resolveKanbanAccentTextColor(column: KanbanBoardColumnConfig): string {
    const normalizedDisplayName = column.displayName.trim().toLowerCase();
    if (normalizedDisplayName.includes("archive")) {
        return token("color.text.danger");
    }
    if (column.textColor.includes("accent")) {
        return column.textColor;
    }
    const accentMatch =
        /accent\.([a-z]+)\.subtlest/i.exec(column.background)
        ?? /accent-([a-z]+)-subtlest/i.exec(column.background);
    if (accentMatch === null) {
        return column.textColor;
    }
    const accent = accentMatch[1]!;
    switch (accent) {
        case "blue":
            return token("color.text.accent.blue");
        case "green":
            return token("color.text.accent.green");
        case "yellow":
            return token("color.text.accent.yellow");
        case "gray":
            return token("color.text.accent.gray");
        case "red":
            return token("color.text.accent.red");
        case "lime":
            return token("color.text.accent.green");
        case "orange":
            return token("color.text.accent.orange");
        case "purple":
            return token("color.text.accent.purple");
        case "teal":
            return token("color.text.accent.teal.bolder");
        case "magenta":
            return token("color.text.accent.magenta");
        default:
            return column.textColor;
    }
}

export function kanbanColumnToListLozengeStyle(column: KanbanBoardColumnConfig): ListCardLozengeStyle {
    const color = resolveKanbanAccentTextColor(column);
    return {
        backgroundColor: column.background,
        color,
    };
}

/** Same `columnConfig` for kanban headers and list status — fixes neutral text on accent backgrounds. */
export function withKanbanAccentTextColors(
    columnConfig: ReadonlyArray<KanbanBoardColumnConfig>,
): Array<KanbanBoardColumnConfig> {
    return columnConfig.map((column) => {
        const textColor = resolveKanbanAccentTextColor(column);
        return textColor === column.textColor ? column : {...column, textColor};
    });
}

/** Synthetic values for list cards when kanban groups by owner id but the pill shows Assigned / Unassigned. */
export const LIST_INQUEUE_ASSIGNMENT_STATUS = {
    assigned: "__list_inqueue_assigned__",
    unassigned: "__list_inqueue_unassigned__",
} as const;

export function getInQueueAssignmentSyntheticStatusValue(isAssigned: boolean): string {
    return isAssigned
        ? LIST_INQUEUE_ASSIGNMENT_STATUS.assigned
        : LIST_INQUEUE_ASSIGNMENT_STATUS.unassigned;
}

/** Maps sidebar status ids (assigned / unassigned) to synthetic in-queue kanban field values. */
export function resolveInQueueAssignmentStatusFilterValues(statusId: string): ReadonlyArray<string> {
    const normalized = statusId.trim().toLowerCase();
    if (normalized === "assigned") {
        return [LIST_INQUEUE_ASSIGNMENT_STATUS.assigned];
    }
    if (normalized === "unassigned") {
        return [LIST_INQUEUE_ASSIGNMENT_STATUS.unassigned];
    }
    return [statusId];
}

export function createInQueueAssignmentListStatusColumnConfig(): Array<KanbanBoardColumnConfig> {
    return [
        {
            id: "f3000001-0001-4001-8001-000000000001" as Uuid,
            displayName: "Assigned",
            background: token("color.background.accent.green.subtlest"),
            textColor: token("color.text.accent.green"),
            possibleValues: [LIST_INQUEUE_ASSIGNMENT_STATUS.assigned],
        },
        {
            id: "f3000001-0001-4001-8001-000000000002" as Uuid,
            displayName: "Unassigned",
            background: token("color.background.accent.yellow.subtlest"),
            textColor: token("color.text.accent.yellow"),
            possibleValues: [LIST_INQUEUE_ASSIGNMENT_STATUS.unassigned, ""],
        },
    ];
}

export function inferListStatusPillType(statusLabel: string): ListStatusPillType {
    const normalized = statusLabel.trim().toLowerCase();
    if (normalized.includes("delete")) {
        return "Deleted";
    }
    if (normalized === "assigned") {
        return "Assigned";
    }
    if (normalized === "unassigned") {
        return "Unassigned";
    }
    if (
        normalized.includes("cancel")
        || normalized.includes("archive")
        || normalized.includes("reject")
        || normalized.includes("duplicate")
        || normalized.includes("non-engaged")
        || normalized.includes("inactive")
        || normalized.includes("churned")
    ) {
        return "Archived";
    }
    return "Active";
}

export function listStatusPillTypeToLozengeStyle(pillType: ListStatusPillType): ListCardLozengeStyle {
    switch (pillType) {
        case "Assigned": {
            const color = token("color.text.accent.green");
            return {
                backgroundColor: token("color.background.accent.green.subtlest"),
                color,
            };
        }
        case "Unassigned": {
            const color = token("color.text.accent.yellow");
            return {
                backgroundColor: token("color.background.accent.yellow.subtlest"),
                color,
            };
        }
        case "Archived": {
            const color = token("color.text.danger");
            return {
                backgroundColor: token("color.background.accent.red.subtlest"),
                color,
            };
        }
        case "Deleted": {
            const color = token("color.text.accent.red");
            return {
                backgroundColor: token("color.background.accent.red.subtlest"),
                color,
            };
        }
        case "Active":
        default: {
            const color = token("color.text.accent.blue");
            return {
                backgroundColor: token("color.background.accent.blue.subtlest"),
                color,
            };
        }
    }
}

function normalizeStatusValue(statusValue: unknown): string {
    if (statusValue === null || statusValue === undefined) {
        return "";
    }
    return String(statusValue).trim();
}

function findListStatusColumn(
    statusValue: string,
    columnConfig: ReadonlyArray<KanbanBoardColumnConfig>,
): KanbanBoardColumnConfig | undefined {
    const normalizedStatusValue = statusValue.toLowerCase();
    return columnConfig.find((column) =>
        column.possibleValues.some(
            (possible) => String(possible).toLowerCase() === normalizedStatusValue,
        ),
    );
}

/**
 * Resolves a list-card status label and lozenge colors from the same `columnConfig` used by kanban.
 */
export function resolveListCardStatusFromColumnConfig({
    statusValue,
    columnConfig,
    resolveDisplayName,
    fallbackDisplayName = "Active",
    fallbackPillType = inferListStatusPillType,
}: {
    statusValue: unknown;
    columnConfig: ReadonlyArray<KanbanBoardColumnConfig>;
    resolveDisplayName?: (params: {
        statusValue: string;
        matchedColumn: KanbanBoardColumnConfig | undefined;
    }) => string;
    fallbackDisplayName?: string;
    fallbackPillType?: (statusLabel: string) => ListStatusPillType;
}): ListCardStatus {
    const raw = normalizeStatusValue(statusValue);
    const matchedColumn = findListStatusColumn(raw, columnConfig);
    const name =
        resolveDisplayName?.({statusValue: raw, matchedColumn})
        ?? matchedColumn?.displayName
        ?? (raw !== "" ? raw : fallbackDisplayName);

    if (matchedColumn !== undefined) {
        return {
            name,
            lozengeStyle: kanbanColumnToListLozengeStyle(matchedColumn),
        };
    }

    return {
        name,
        lozengeStyle: listStatusPillTypeToLozengeStyle(fallbackPillType(name)),
    };
}

export function createListStatusResolver<T>({
    columnConfig,
    getStatusValue,
    resolveDisplayName,
    fallbackDisplayName,
    fallbackPillType,
}: {
    columnConfig: ReadonlyArray<KanbanBoardColumnConfig>;
    getStatusValue: (row: T) => unknown;
    resolveDisplayName?: (params: {
        row: T;
        statusValue: string;
        matchedColumn: KanbanBoardColumnConfig | undefined;
    }) => string;
    fallbackDisplayName?: string | ((row: T) => string);
    fallbackPillType?: (statusLabel: string) => ListStatusPillType;
}): (row: T) => ListCardStatus {
    return (row) => {
        const statusValue = getStatusValue(row);
        const fallbackName =
            typeof fallbackDisplayName === "function" ? fallbackDisplayName(row) : fallbackDisplayName;
        return resolveListCardStatusFromColumnConfig({
            statusValue,
            columnConfig,
            fallbackDisplayName: fallbackName,
            fallbackPillType,
            resolveDisplayName:
                resolveDisplayName === undefined
                    ? undefined
                    : ({statusValue: raw, matchedColumn}) =>
                          resolveDisplayName({row, statusValue: raw, matchedColumn}),
        });
    };
}
